import React, { useEffect, useState, useRef } from 'react';
import { Box, Fade } from '@mui/material';
import {
    Instagram as InstagramIcon,
    Facebook as FacebookIcon,
    YouTube as YouTubeIcon,
    Twitter as TwitterIcon,
    LinkedIn as LinkedInIcon,
    Reddit as RedditIcon,
    MusicNote as TikTokIcon,
} from '@mui/icons-material';
import { keyframes } from '@emotion/react';
import { subscribe } from '../services/loaderTracker';

const SHOW_DELAY_MS = 180;
const HIDE_DELAY_MS = 120;

const ICONS = [
    { Icon: InstagramIcon,  color: '#E1306C', bg: 'linear-gradient(135deg,#F58529 0%,#DD2A7B 50%,#8134AF 100%)' },
    { Icon: FacebookIcon,   color: '#fff',    bg: '#1877F2' },
    { Icon: YouTubeIcon,    color: '#fff',    bg: '#FF0000' },
    { Icon: TwitterIcon,    color: '#fff',    bg: '#0F1419' },
    { Icon: LinkedInIcon,   color: '#fff',    bg: '#0A66C2' },
    { Icon: RedditIcon,     color: '#fff',    bg: '#FF4500' },
    { Icon: TikTokIcon,     color: '#fff',    bg: 'linear-gradient(135deg,#25F4EE 0%,#000 50%,#FE2C55 100%)' },
];

const bounce = keyframes`
  0%, 100% { transform: translateY(0) scale(1); filter: brightness(1); }
  40%      { transform: translateY(-22px) scale(1.12); filter: brightness(1.25); }
  60%      { transform: translateY(-18px) scale(1.08); filter: brightness(1.15); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.5; transform: scaleX(0.7); }
  50%      { opacity: 1;   transform: scaleX(1); }
`;

const shimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const ring = keyframes`
  0%   { transform: rotate(0deg);   opacity: 0.35; }
  100% { transform: rotate(360deg); opacity: 0.35; }
`;

export default function GlobalLoader() {
    const [visible, setVisible] = useState(false);
    const visibleRef = useRef(false);
    const showTimer = useRef(null);
    const hideTimer = useRef(null);

    useEffect(() => {
        const setVis = (v) => {
            visibleRef.current = v;
            setVisible(v);
        };
        const unsub = subscribe((isLoading) => {
            if (isLoading) {
                if (hideTimer.current) {
                    clearTimeout(hideTimer.current);
                    hideTimer.current = null;
                }
                if (!visibleRef.current && !showTimer.current) {
                    showTimer.current = setTimeout(() => {
                        showTimer.current = null;
                        setVis(true);
                    }, SHOW_DELAY_MS);
                }
            } else {
                if (showTimer.current) {
                    clearTimeout(showTimer.current);
                    showTimer.current = null;
                }
                if (visibleRef.current && !hideTimer.current) {
                    hideTimer.current = setTimeout(() => {
                        hideTimer.current = null;
                        setVis(false);
                    }, HIDE_DELAY_MS);
                }
            }
        });
        return () => {
            unsub();
            if (showTimer.current) clearTimeout(showTimer.current);
            if (hideTimer.current) clearTimeout(hideTimer.current);
        };
    }, []);

    return (
        <Fade in={visible} timeout={{ enter: 220, exit: 180 }} unmountOnExit>
            <Box
                role="status"
                aria-live="polite"
                aria-label="Loading"
                sx={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: (t) => t.zIndex.modal + 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backdropFilter: 'blur(6px)',
                    WebkitBackdropFilter: 'blur(6px)',
                    background: 'radial-gradient(ellipse at center, rgba(15,23,42,0.55) 0%, rgba(15,23,42,0.75) 100%)',
                    pointerEvents: 'auto',
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        width: { xs: 280, sm: 340 },
                        p: 3,
                        borderRadius: '22px',
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.04) inset',
                    }}
                >
                    {/* decorative rotating ring */}
                    <Box
                        aria-hidden
                        sx={{
                            position: 'absolute',
                            inset: -2,
                            borderRadius: '24px',
                            background: 'conic-gradient(from 0deg, #22C55E, #3B82F6, #E1306C, #FF4500, #22C55E)',
                            filter: 'blur(14px)',
                            animation: `${ring} 3.6s linear infinite`,
                            zIndex: -1,
                        }}
                    />

                    {/* bouncing icon row */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-end',
                            justifyContent: 'space-between',
                            gap: 0.5,
                            height: 72,
                            mb: 2,
                        }}
                    >
                        {ICONS.map(({ Icon, color, bg }, i) => (
                            <Box
                                key={i}
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '10px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: bg,
                                    boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
                                    animation: `${bounce} 1.2s ease-in-out infinite`,
                                    animationDelay: `${i * 0.11}s`,
                                    willChange: 'transform',
                                }}
                            >
                                <Icon sx={{ color, fontSize: 22 }} />
                            </Box>
                        ))}
                    </Box>

                    {/* shimmer progress bar */}
                    <Box
                        aria-hidden
                        sx={{
                            height: 4,
                            borderRadius: 2,
                            mb: 1.5,
                            background: 'linear-gradient(90deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.18) 50%, rgba(255,255,255,0.06) 100%)',
                            backgroundSize: '200% 100%',
                            animation: `${shimmer} 1.6s ease-in-out infinite`,
                        }}
                    />

                    {/* caption */}
                    <Box
                        sx={{
                            textAlign: 'center',
                            color: 'rgba(255,255,255,0.85)',
                            fontFamily: '"DM Sans","Inter",sans-serif',
                            fontWeight: 600,
                            letterSpacing: '0.04em',
                            fontSize: 13,
                        }}
                    >
                        <Box component="span" sx={{ animation: `${pulse} 1.2s ease-in-out infinite`, display: 'inline-block', transformOrigin: 'center' }}>
                            Syncing your socials…
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Fade>
    );
}
